import React, { useState, useEffect } from 'react';
import { X, AlertCircle } from 'lucide-react';

const UpdatePaymentModal = ({
  updatePayment,
  setUpdatePayment,
  updatePaymentLoading,
  handleUpdatePayment,
  handleCloseUpdateForm
}) => {
  const [calculatedBalance, setCalculatedBalance] = useState(0);
  const [validationErrors, setValidationErrors] = useState({});

  // Validate paid amount
  const validatePaidAmount = (paidAmount, totalAmount) => {
    const errors = {};
    
    if (paidAmount && totalAmount) {
      const paid = parseFloat(paidAmount);
      const total = parseFloat(totalAmount);
      
      if (paid > total) {
        errors.paid_amount = 'Paid amount cannot exceed total amount';
      }
      
      if (paid < 0) {
        errors.paid_amount = 'Paid amount cannot be negative';
      }
    }
    
    return errors;
  };

  useEffect(() => {
    const total = parseFloat(updatePayment.total_amount) || 0;
    const originalPaid = parseFloat(updatePayment.original_paid_amount) || 0;
    const newPaid = parseFloat(updatePayment.paid_amount) || 0;
    
    // Validate on mount
    if (updatePayment.paid_amount) {
      const errors = validatePaidAmount(updatePayment.paid_amount, updatePayment.total_amount);
      setValidationErrors(errors);
    }
    
    if (updatePayment.paid_amount !== '') {
      setCalculatedBalance(total - newPaid);
    } else {
      setCalculatedBalance(total - originalPaid);
    }
  }, [updatePayment.total_amount, updatePayment.paid_amount, updatePayment.original_paid_amount]);

  const handleUpdateInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'paid_amount') {
      const paidValue = parseFloat(value) || 0;
      const total = parseFloat(updatePayment.total_amount) || 0;
      const newBalance = total - paidValue;
      
      // Validate paid amount
      const errors = validatePaidAmount(value, updatePayment.total_amount);
      setValidationErrors(errors);
      
      setUpdatePayment(prev => ({ 
        ...prev, 
        [name]: value,
        balance_amount: newBalance
      }));
      
      setCalculatedBalance(newBalance);
    } else {
      setUpdatePayment(prev => ({ 
        ...prev, 
        [name]: value 
      }));
    }
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate before submit
    const errors = validatePaidAmount(updatePayment.paid_amount, updatePayment.total_amount);
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    handleUpdatePayment(e);
  };

  // Check if form is valid
  const isFormValid = () => {
    return (
      updatePayment.payment_id &&
      updatePayment.paid_amount && 
      parseFloat(updatePayment.paid_amount) >= 0 &&
      parseFloat(updatePayment.paid_amount) <= parseFloat(updatePayment.total_amount)
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header - Sticky section */}
        <div className="sticky top-0 bg-white z-20 border-b">
          <div className="flex justify-between items-center p-6">
            <h2 className="text-xl font-bold text-gray-800">Update Payment Details</h2>
            <button 
              onClick={handleCloseUpdateForm}
              disabled={updatePaymentLoading}
              className="disabled:opacity-50 text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Form content */}
        <div className="p-6 space-y-4">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-700 mb-3">Payment Information (Read Only)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment ID</label>
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900">
                    {updatePayment.payment_id}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice No</label>
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900">
                    {updatePayment.invoice_no}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">License ID</label>
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900">
                    {updatePayment.license_id}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Plan</label>
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900">
                    {updatePayment.payment_plan}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount (LKR)</label>
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 font-semibold">
                    {parseFloat(updatePayment.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Paid Amount (LKR)</label>
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 font-semibold">
                    {parseFloat(updatePayment.original_paid_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Balance (LKR)</label>
                  <div className={`w-full px-3 py-2 border rounded-lg font-semibold ${
                    parseFloat(updatePayment.balance_amount || 0) > 0 
                      ? 'bg-red-50 border-red-200 text-red-700' 
                      : 'bg-green-50 border-green-200 text-green-700'
                  }`}>
                    {parseFloat(updatePayment.balance_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900">
                    {updatePayment.status}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Created Date</label>
                  <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900">
                    {updatePayment.created_date}
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold text-gray-700 mb-3">Editable Fields</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">New Paid Amount (LKR) *</label>
                  <div className="relative">
                    <input
                      type="number"
                      name="paid_amount"
                      value={updatePayment.paid_amount}
                      onChange={handleUpdateInputChange}
                      required
                      disabled={updatePaymentLoading}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                        validationErrors.paid_amount ? 'border-red-500' : 'border-gray-300'
                      }`}
                      step="0.01"
                      min="0"
                      max={updatePayment.total_amount}
                      placeholder="Enter new paid amount"
                    />
                    {/* <div className="absolute right-3 top-2.5 text-gray-400">
                      LKR
                    </div> */}
                  </div>
                  {validationErrors.paid_amount ? (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {validationErrors.paid_amount}
                    </p>
                  ) : updatePayment.total_amount && updatePayment.paid_amount ? (
                    <div className="text-xs mt-1">
                      <span className="text-gray-600">Remaining: </span>
                      <span className={`font-medium ${
                        calculatedBalance > 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        LKR {calculatedBalance.toFixed(2)}
                      </span>
                    </div>
                  ) : null}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">New Balance (LKR)</label>
                  <input
                    type="text"
                    value={calculatedBalance.toFixed(2)}
                    readOnly
                    className={`w-full px-3 py-2 border rounded-lg font-semibold ${
                      calculatedBalance > 0 
                        ? 'bg-red-50 border-red-200 text-red-700' 
                        : 'bg-green-50 border-green-200 text-green-700'
                    }`}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {calculatedBalance > 0 ? 'Balance outstanding' : 'Payment completed'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method *</label>
                  <select
                    name="payment_method"
                    value={updatePayment.payment_method}
                    onChange={handleUpdateInputChange}
                    required
                    disabled={updatePaymentLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                  >
                    <option value="Cash">Cash</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Online">Online</option>
                    <option value="Cheque">Cheque</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
                  <textarea
                    name="remarks"
                    value={updatePayment.remarks}
                    onChange={handleUpdateInputChange}
                    disabled={updatePaymentLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    rows="3"
                    placeholder="Enter payment remarks (text only)"
                  />
                </div>
              </div>
            </div>

            {/* Validation summary */}
            {Object.keys(validationErrors).length > 0 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 text-red-700 mb-2">
                  <AlertCircle size={16} />
                  <span className="font-medium">Validation Errors:</span>
                </div>
                <ul className="text-sm text-red-600 list-disc pl-5 space-y-1">
                  {Object.values(validationErrors).map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleCloseUpdateForm}
                disabled={updatePaymentLoading}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updatePaymentLoading || !isFormValid()}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-green-300 disabled:cursor-not-allowed font-medium"
              >
                {updatePaymentLoading ? 'Updating...' : 'Update Payment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdatePaymentModal;